import React, { useState, useRef } from "react";
import { Save } from "lucide-react";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const INQUIRY_TYPES = [
  {
    value: "Colocation",
    label: "Colocation",
    helpText: "Requests related to colocation on an existing tower",
  },
  {
    value: "General",
    label: "General",
    helpText: "General inquiries",
  },
  {
    value: "Landlord",
    label: "Landlord",
    helpText: "Inquiries related to existing landlords with active leases with Tillman Infrastructure",
  },
  {
    value: "Site Maintenance",
    label: "Site Maintenance",
    helpText: "Report or request maintenance on an existing site",
  },
  {
    value: "Site Proposal",
    label: "Site Proposal",
    helpText: "Propose a site location for a new tower",
  },
];

const INQUIRY_TYPES_WITH_SITE = ["Colocation", "General", "Landlord"];

const LANDLORD_CONFIRMATION_TEXT = "Selecting 'Ok' confirms you are an EXISTING landlord for an EXISTING site. For new site inquiries, use 'Site Proposal' instead.";

const PARAMS = new URLSearchParams(window.location.search);
const ALLOWED_TYPES_PARAM = PARAMS.get("types");

const ALLOWED_TYPES = ALLOWED_TYPES_PARAM
  ? ALLOWED_TYPES_PARAM.split(",")
  : INQUIRY_TYPES.map((t) => t.value); // default: show all

const INQUIRY_TYPES_FILTERED = INQUIRY_TYPES.filter((t) =>
  ALLOWED_TYPES.includes(t.value),
);

const descriptionHelpText =
  "Please describe the issue or opportunity you are inquiring for";

const latLongHelpText =
  "Decimals are required for latitude & longitude. Please do not use any letters - only numbers. (e.g., 28.123456, -82.123456)";

const siteNameIdHelpText = 
  "Format: TI-XXXXX (e.g., TI-00016) or Site Name";

const tiOppHelpText =
  "Format: TI-OPP-XXXXX (e.g., TI-OPP-00013)";

const asrHelpText = 
  "Format: XXXXXXX (e.g., 1639807)";

const API_BASE_URL = process.env.REACT_APP_FUNCTION_APP_URL;
const FUNCTION_KEY = process.env.REACT_APP_FUNCTION_KEY;
const basicInformation = "Contact Information";
const inquiryInformation = "Inquiry Information";
const friendlySiteInformation = "Site Proposal Information";
const siteMaintenanceInformation = "Site Maintenance Address";
const landlordInformation = "Landlord Site Address";

const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent";

function AddressFields({
  heading,
  formData,
  handleChange,
  handleBlur,
  disabled,
  required = false,
  requireLatitude = required,
  requireLongitude = required,
}) {
  const mark = required ? <span className="required">*</span> : null;
  const latMark = requireLatitude ? <span className="required">*</span> : null;
  const longMark = requireLongitude ? <span className="required">*</span> : null;
  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
        {heading}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Street Address {mark}</label>
          <input
            type="text"
            name="Street_Address__c"
            value={formData.Street_Address__c}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City {mark}</label>
          <input
            type="text"
            name="City__c"
            value={formData.City__c}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code {mark}</label>
          <input
            type="text"
            name="Zip_Code__c"
            value={formData.Zip_Code__c}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">County {mark}</label>
          <input
            type="text"
            name="County__c"
            value={formData.County__c}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State {mark}</label>
          <select
            name="State__c"
            value={formData.State__c}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            className={inputClass}
          >
            <option value="">-- Select --</option>
            {US_STATES.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Latitude {latMark}</label>
          <input
            type="text"
            name="Latitude__c"
            value={formData.Latitude__c}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            required={requireLatitude}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Longitude {longMark}</label>
          <input
            type="text"
            name="Longitude__c"
            value={formData.Longitude__c}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            required={requireLongitude}
            className={inputClass}
          />
        </div>
        <p className="col-span-2 text-xs text-gray-500 mt-1">{latLongHelpText}</p>
      </div>
    </section>
  );
}

export default function TillmanConnectForm() {
  const [formData, setFormData] = useState({
    First_Name__c: "",
    Last_Name__c: "",
    Phone_Number__c: "",
    Email__c: "",
    Inquiry_Type__c:
      INQUIRY_TYPES_FILTERED.length === 1
        ? INQUIRY_TYPES_FILTERED[0].value
        : "",
    Description__c: "",
    Street_Address__c: "",
    City__c: "",
    County__c: "",
    State__c: "",
    Zip_Code__c: "",
    Latitude__c: "",
    Longitude__c: "",
    Tillman_Site_Id__c: "",
    TI_OPP_ID__c: "",
    ASR__c: ""
  });

  const formLoadTime = useRef(Date.now());
  const [errorMessage, setErrorMessage] = useState({ text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLocked, setFormLocked] = useState(false);
  const [inquiryCreationError, setInquiryCreationError] = useState(false);
  const [inquiryName, setInquiryName] = useState("");
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [attachments, setAttachments] = useState([
    {
      id: "supportingDocuments",
      files: [],
      name: "Supporting Documents",
    },
  ]);
  const [inquiryTypeOpen, setInquiryTypeOpen] = useState(false);
  const [inquiryTypeHovered, setInquiryTypeHovered] = useState(null);

  const showSubmitButton = !isSubmitting && !inquiryName && !formLocked;

  const handleChange = (e) => {
    let name = e.target.name;
    let value;
    if (e.target.type === "checkbox") {
      value = e.target.checked;
    } else {
      value = e.target.value;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlur = (e) => {
    if (e.target.type !== "checkbox") {
      const name = e.target.name;
      setFormData((prev) => ({
        ...prev,
        [name]: typeof prev[name] === "string" ? prev[name].trim() : prev[name],
      }));
    }
  };

  const handleWebsiteUpdated = () => {
    setFormLocked(true);
  };

  const validateEmail = (email) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  const validatePhone = (phone) => {
    const pattern = /^\d{10}$/;
    return pattern.test(phone.replace(/\D/g, ""));
  };

  const validateLatitude = (lat) => {
    return lat >= -90 && lat <= 90 && Number.isFinite(lat);
  };

  const validateLongitude = (lng) => {
    return lng >= -180 && lng <= 180 && Number.isFinite(lng);
  };

  const convertFilesToBase64 = async () => {
    const allFiles = attachments.flatMap((attachment) =>
      attachment.files.map((file) => ({ file, attachmentId: attachment.id })),
    );

    const converted = await Promise.all(
      allFiles.map(
        ({ file }) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                fileName: file.name,
                base64File: reader.result.split(",")[1],
              });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }),
      ),
    );

    return converted;
  };

  async function submitInquiry(event) {
    event.preventDefault();

    const timeElapsed = (Date.now() - formLoadTime.current) / 1000;

    if (timeElapsed < 2) {
      setErrorMessage({ text: "Form submitted too quickly." });
      setFormLocked(true);
      return;
    }

    setErrorMessage({ text: "" });
    setIsSubmitting(true);

    if (formData.Inquiry_Type__c === null || formData.Inquiry_Type__c === "") {
      setErrorMessage({ text: "Please enter the Inquiry Type" });
      setIsSubmitting(false);
      return;
    }

    if (!validatePhone(formData.Phone_Number__c)) {
      setErrorMessage({ text: "Please enter a valid 10-digit phone number." });
      setIsSubmitting(false);
      return;
    }

    if (!validateEmail(formData.Email__c)) {
      setErrorMessage({ text: "Please enter a valid email address." });
      setIsSubmitting(false);
      return;
    }

    if (formData.Latitude__c && !formData.Latitude__c.includes(".")) {
      setErrorMessage({ text: "Latitude must include a decimal point." });
      setIsSubmitting(false);
      return;
    }

    if (
      formData.Latitude__c &&
      !validateLatitude(Number(formData.Latitude__c))
    ) {
      setErrorMessage({ text: "Please enter a valid latitude (-90 to 90)." });
      setIsSubmitting(false);
      return;
    }

    if (formData.Longitude__c && !formData.Longitude__c.includes(".")) {
      setErrorMessage({ text: "Longitude must include a decimal point." });
      setIsSubmitting(false);
      return;
    }

    if (
      formData.Longitude__c &&
      !validateLongitude(Number(formData.Longitude__c))
    ) {
      setErrorMessage({
        text: "Please enter a valid longitude (-180 to 180).",
      });
      setIsSubmitting(false);
      return;
    }

    const payload = {
      ...formData,
      Latitude__c: formData.Latitude__c ? Number(formData.Latitude__c) : null,
      Longitude__c: formData.Longitude__c
        ? Number(formData.Longitude__c)
        : null,
    };

    const files = await convertFilesToBase64();

    try {
      const createInquiryResult = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Ocp-Apim-Subscription-Key": FUNCTION_KEY,
        },
        body: JSON.stringify({
          tiInquiry: payload,
          files: files,
        }),
      });

      if (createInquiryResult.status === 200) {
        const data = await createInquiryResult.json();
        console.log(data);
        setInquiryName(data.Name);
        setIsSubmitting(false);
      } else {
        const data = await createInquiryResult.json();
        console.log(data);
        setErrorMessage({text: `Please share these technical details with your Tillman contact: ${data.message || 'Error: ' + createInquiryResult.status}`,
        });
        setInquiryCreationError(true);
        setIsSubmitting(false);
      }
    } catch (error) {
      setErrorMessage({text: `Please share these technical details with your Tillman contact: ${error.message}`,});
      setInquiryCreationError(true);
      setIsSubmitting(false);
      console.log(error);
    }
  }

  const handleFileUpload = (e, fileType) => {
    const uploadedfiles = e.target.files;
    const maxSize = 30 * 1024 * 1024;
    const maxTotalFileSize = 99 * 1024 * 1024;
    const validFiles = [];
    const invalidFiles = [];

    let tempTotalFileSize = totalFileSize;
    Array.from(uploadedfiles).forEach((file) => {
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
      } else if (tempTotalFileSize + file.size > maxTotalFileSize) {
        alert(
          `File(s) can't be uploaded because the total size of all files cannot exceed 99 MB`,
        );
        return;
      } else {
        validFiles.push(file);
        tempTotalFileSize += file.size;
      }
    });

    setTotalFileSize(tempTotalFileSize);

    if (invalidFiles.length > 0) {
      alert(
        `The following files exceed 30 MB and were not uploaded:\n${invalidFiles.join(
          "\n",
        )}`,
      );
    } else {
      const newAttachments = attachments.map((attachment) =>
        attachment.id === fileType
          ? { ...attachment, files: [...attachment.files, ...validFiles] }
          : attachment,
      );
      setAttachments(newAttachments);
    }

    e.target.value = "";
  };

  const removeFile = (fileType, index = null) => {
    const newAttachments = attachments.map((attachment) =>
      attachment.id === fileType
        ? {
            ...attachment,
            files: attachment.files.filter((_, i) => i !== index),
          }
        : attachment,
    );
    setAttachments(newAttachments);
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <form onSubmit={submitInquiry} className="p-6 space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                {basicInformation}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input /*Honeypot Field - Hidden from Field*/
                  type="text"
                  name="website"
                  onChange={handleWebsiteUpdated}
                  className="absolute opacity-0 pointer-events-none"
                  tabIndex="-1"
                  autoComplete="new-password"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="First_Name__c"
                    value={formData.First_Name__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={inquiryName}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="Last_Name__c"
                    value={formData.Last_Name__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={inquiryName}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="Phone_Number__c"
                    value={formData.Phone_Number__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={inquiryName}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="Email__c"
                    value={formData.Email__c}
                    onChange={handleChange}
                    disabled={inquiryName}
                    required
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                {inquiryInformation}
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Inquiry Type <span className="required">*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => {
                        if (!inquiryName) setInquiryTypeOpen(!inquiryTypeOpen);
                      }}
                      style={{
                        border: `1px solid ${
                          inquiryTypeOpen ? "#3b82f6" : "#d1d5db"
                        }`,
                        borderRadius: inquiryTypeOpen ? "6px 6px 0 0" : "6px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        backgroundColor: inquiryName ? "#f9fafb" : "white",
                        cursor: inquiryName ? "not-allowed" : "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        userSelect: "none",
                        boxShadow: inquiryTypeOpen
                          ? "0 0 0 3px rgba(59,130,246,0.15)"
                          : "none",
                      }}
                    >
                      {formData.Inquiry_Type__c ? (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flex: 1,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 500,
                              color: "#111827",
                              minWidth: "120px",
                            }}
                          >
                            {
                              INQUIRY_TYPES_FILTERED.find(
                                (o) => o.value === formData.Inquiry_Type__c,
                              )?.label
                            }
                          </span>
                          <span style={{ color: "#6b7280", fontSize: "13px" }}>
                            —{" "}
                            {
                              INQUIRY_TYPES_FILTERED.find(
                                (o) => o.value === formData.Inquiry_Type__c,
                              )?.helpText
                            }
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>-- Select --</span>
                      )}
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#9ca3af",
                          marginLeft: "12px",
                          flexShrink: 0,
                          transform: inquiryTypeOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        ▼
                      </span>
                    </div>

                    {inquiryTypeOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "white",
                          border: "1px solid #3b82f6",
                          borderTop: "none",
                          borderRadius: "0 0 6px 6px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          zIndex: 100,
                          overflow: "hidden",
                        }}
                      >
                        {INQUIRY_TYPES_FILTERED.map((o, i) => (
                          <div
                            key={o.value}
                            onMouseEnter={() => setInquiryTypeHovered(o.value)}
                            onMouseLeave={() => setInquiryTypeHovered(null)}
                            onClick={() => {
                              if (o.value == 'Landlord') {
                                if (window.confirm(LANDLORD_CONFIRMATION_TEXT)) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    Inquiry_Type__c: o.value
                                  }));
                                  setInquiryTypeOpen(false);
                                  setInquiryTypeHovered(null);
                                }
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  Inquiry_Type__c: o.value,
                                }));
                                setInquiryTypeOpen(false);
                                setInquiryTypeHovered(null);
                                }
                              }}
                            
                            style={{
                              padding: "10px 12px",
                              cursor: "pointer",
                              backgroundColor:
                                inquiryTypeHovered === o.value
                                  ? "#eff6ff"
                                  : formData.Inquiry_Type__c === o.value
                                    ? "#f0f9ff"
                                    : "white",
                              borderTop: i > 0 ? "1px solid #f3f4f6" : "none",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color:
                                  inquiryTypeHovered === o.value
                                    ? "#1e40af"
                                    : formData.Inquiry_Type__c === o.value
                                      ? "#1d4ed8"
                                      : "#111827",
                                minWidth: "120px",
                                flexShrink: 0,
                              }}
                            >
                              {o.label}
                              {formData.Inquiry_Type__c === o.value && (
                                <span
                                  style={{
                                    marginLeft: "6px",
                                    fontSize: "12px",
                                    color: "#3b82f6",
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </span>
                            <span style={{ color: "#d1d5db", flexShrink: 0 }}>
                              —
                            </span>
                            <span
                              style={{
                                fontSize: "13px",
                                color:
                                  inquiryTypeHovered === o.value
                                    ? "#3b82f6"
                                    : "#6b7280",
                                lineHeight: "1.4",
                              }}
                            >
                              {o.helpText}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {inquiryTypeOpen && (
                    <div
                      onClick={() => setInquiryTypeOpen(false)}
                      style={{ position: "fixed", inset: 0, zIndex: 99 }}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="required">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">{descriptionHelpText}</p>
                  <textarea
                    name="Description__c"
                    value={formData.Description__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={inquiryName}
                    required
                    rows={1}
                    className={inputClass}
                  />
                </div>

                {INQUIRY_TYPES_WITH_SITE.includes(formData.Inquiry_Type__c) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tillman Site ID/Name
                      </label>
                      <input
                        type="text"
                        name="Tillman_Site_Id__c"
                        value={formData.Tillman_Site_Id__c}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={inquiryName}
                        className={inputClass}
                      />
                      <p className="text-xs text-gray-500 mb-2">{siteNameIdHelpText}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        TI-OPP-ID
                      </label>
                      <input
                        type="text"
                        name="TI_OPP_ID__c"
                        value={formData.TI_OPP_ID__c}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={inquiryName}
                        className={inputClass}
                      />
                      <p className="text-xs text-gray-500 mb-2">{tiOppHelpText}</p>
                    </div>
                  </div>
                )}
                {formData.Inquiry_Type__c === "Site Maintenance" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tillman Site ID/Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="Tillman_Site_Id__c"
                        value={formData.Tillman_Site_Id__c}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={inquiryName}
                        required
                        className={inputClass}
                      />
                      <p className="text-xs text-gray-500 mb-2">{siteNameIdHelpText}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ASR Registration Number
                      </label>
                      <input
                        type="text"
                        name="ASR__c"
                        value={formData.ASR__c}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={inquiryName}
                        className={inputClass}
                      />
                      <p className="text-xs text-gray-500 mb-2">{asrHelpText}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        TI-OPP-ID
                      </label>
                      <input
                        type="text"
                        name="TI_OPP_ID__c"
                        value={formData.TI_OPP_ID__c}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={inquiryName}
                        className={inputClass}
                      />
                      <p className="text-xs text-gray-500 mb-2">{tiOppHelpText}</p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {formData.Inquiry_Type__c === "Site Proposal" && (
              <AddressFields
                heading={friendlySiteInformation}
                formData={formData}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={inquiryName}
              />
            )}

            {formData.Inquiry_Type__c === "Site Maintenance" && (
              <AddressFields
                heading={siteMaintenanceInformation}
                formData={formData}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={inquiryName}
              />
            )}

            {formData.Inquiry_Type__c === "Landlord" && (
              <AddressFields
                heading={landlordInformation}
                formData={formData}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={inquiryName}
                required
                requireLatitude={false}
                requireLongitude={false}
              />
            )}

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                Supporting Documents
              </h2>
              <div className="space-y-4">
                {attachments.map((fileType) => (
                  <div key={fileType.id}>
                    <p className="text-xs text-gray-500 mb-2">
                      File types accepted: PDF, ZIP, JPG, PNG, HEIC, Word,
                      Excel, CSV, TXT. Maximum file size: 30 MB
                    </p>
                    <div className="flex items-center gap-3">
                      <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                        <span className="text-sm text-gray-600">Add Files</span>
                        <input
                          key={fileType.id}
                          type="file"
                          multiple
                          onChange={(e) => handleFileUpload(e, fileType.id)}
                          className="hidden"
                          disabled={inquiryName}
                          accept=".pdf,.zip,.jpg,.jpeg,.png,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt"
                        />
                      </label>
                    </div>
                    {fileType.files.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {fileType.files.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200"
                          >
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="text-sm font-medium text-gray-800">
                                  {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / 1024).toFixed(2)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(fileType.id, index)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {showSubmitButton && (
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting || formLocked}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#09173e] text-white font-medium rounded-md hover:bg-[#122a5e] transition-colors focus:ring-2 focus:ring-[#09173e] focus:ring-offset-2 disabled:bg-[#09173e]/50 disabled:cursor-not-allowed"
                >
                  <Save size={18} />
                  Submit
                </button>
              </div>
            )}

            <div className="pt-4 border-t">
              {isSubmitting && (
                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p className="text-sm text-yellow-800 font-medium flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-yellow-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Please wait... Your inquiry is being saved. Do not leave or
                    refresh the page.
                  </p>
                </div>
              )}

              {inquiryName && (
                <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-400 rounded">
                  <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                    Form Submitted! A Tillman Infrastructure member will reach
                    out once your request has been reviewed. Please refer to:{" "}
                    {inquiryName}.
                  </p>
                </div>
              )}

              {inquiryCreationError && (
                <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded">
                  <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                    Creation Failed
                  </p>
                  {errorMessage.text && (
                    <p className="text-sm text-red-800 mt-2">
                      {errorMessage.text}
                      </p>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
